import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/500.css";
import "@fontsource/playfair-display/600.css";
import "@fontsource/playfair-display/700.css";
import "@fontsource/public-sans/300.css";
import "@fontsource/public-sans/400.css";
import "@fontsource/public-sans/500.css";
import "@fontsource/public-sans/600.css";
import "@fontsource/public-sans/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@/App.css";

import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";

// Public Pages
import { HomePage } from "./pages/HomePage";
import { CategoryPage } from "./pages/CategoryPage";
import { ArticlePage } from "./pages/ArticlePage";
import { SubmitPage } from "./pages/SubmitPage";
import { SearchPage } from "./pages/SearchPage";
import { AboutPage } from "./pages/AboutPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { ContactPage } from "./pages/ContactPage";
import { AccessibilityPage } from "./pages/AccessibilityPage";

// Staff Pages (Hidden Admin Portal)
import { AdminLogin } from "./pages/admin/AdminLogin";
import { AdminArticles } from "./pages/admin/AdminArticles";
import { ArticleEditor } from "./pages/admin/ArticleEditor";
import { AdminSubmissions } from "./pages/admin/AdminSubmissions";
import { AdminMedia } from "./pages/admin/AdminMedia";
import { AdminTrendingVideos } from "./pages/admin/AdminTrendingVideos";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminAnalytics } from "./pages/admin/AdminAnalytics";
import { AdminNewsletter } from "./pages/admin/AdminNewsletter";
import { AdminComments } from "./pages/admin/AdminComments";
import { AdminSettings } from "./pages/admin/AdminSettings";
import { AdminAuditLogs } from "./pages/admin/AdminAuditLogs";
import { SetupPassword } from "./pages/admin/SetupPassword";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/article/:slug" element={<ArticlePage />} />
        <Route path="/submit" element={<SubmitPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        
        {/* Staff Routes (Hidden Admin Portal) */}
        <Route path="/staff/login" element={<AdminLogin />} />
        <Route path="/staff/setup-password" element={<SetupPassword />} />
        <Route path="/staff" element={<Navigate to="/staff/articles" replace />} />
        <Route
          path="/staff/articles"
          element={
            <ProtectedRoute>
              <AdminArticles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/articles/new"
          element={
            <ProtectedRoute>
              <ArticleEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/articles/:id"
          element={
            <ProtectedRoute>
              <ArticleEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/trending-videos"
          element={
            <ProtectedRoute>
              <AdminTrendingVideos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/submissions"
          element={
            <ProtectedRoute>
              <AdminSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/media"
          element={
            <ProtectedRoute>
              <AdminMedia />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/users"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/analytics"
          element={
            <ProtectedRoute>
              <AdminAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/newsletter"
          element={
            <ProtectedRoute>
              <AdminNewsletter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/comments"
          element={
            <ProtectedRoute>
              <AdminComments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/settings"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/audit-logs"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminAuditLogs />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
